import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDealRoomBrandedName, getDealRoomSingleWord } from '../../../utils/locale';
import { CLOSE_DEAL } from '../../../graphql/mutations/dealMutations';

// Try to import SuccessionThemeProvider, fall back gracefully if not available
let useSuccessionTheme;
try {
  const SuccessionThemeModule = require('../SuccessionThemeProvider');
  useSuccessionTheme = SuccessionThemeModule.useSuccessionTheme;
} catch (error) {
  // SuccessionThemeProvider not available, we'll handle this gracefully
  useSuccessionTheme = null;
}

const DealMenu = ({ deal, companySettings = {}, activeTab }) => {
  const intl = useIntl();
  const location = useLocation();
  const currentPath = location.pathname;
  const dealRoomBrandedName = getDealRoomBrandedName(companySettings) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });
  const dealRoomSingleWord = getDealRoomSingleWord(companySettings) || 'Room';
  
  // Just check if theme provider is available - no need for complex inline styling
  const hasThemeProvider = useSuccessionTheme !== null;
  
  const [closeDeal] = useMutation(CLOSE_DEAL, {
    onError: (error) => console.error("Error closing deal:", error)
  });
  
  // Return loading state if deal is undefined
  if (!deal) {
    return (
      <div className="mb-6 bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession p-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-succession-bg-secondary rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-succession-bg-secondary rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Count deal interests by workflow state
  const getCount = (dealInterestsArray) => {
    if (!dealInterestsArray) return 0;
    return dealInterestsArray.length;
  };
  
  // Safe navigation helper
  const safeValue = (obj, path, defaultValue = 0) => {
    if (!obj) return defaultValue;
    
    const keys = path.split('.');
    let value = obj;
    
    for (const key of keys) {
      if (value === null || value === undefined) return defaultValue;
      value = value[key];
    }
    
    return value || defaultValue;
  };
  
  const isActive = (path) => {
    if (activeTab && path.includes(activeTab)) {
      return true;
    }
    return currentPath.includes(path);
  };
  
  // Extract safe values from deal object
  const dealName = deal.deal_name || '';
  const isClosed = Boolean(deal.closed);
  const matchesCount = safeValue(deal, 'matches_count', 0);
  const invitesCount = safeValue(deal, 'number_of_invites', 0);
  const proposalsCount = safeValue(deal, 'number_of_proposals', 0);
  const shortlistedCount = safeValue(deal, 'shortlisted.length', 0);
  const ndasCount = safeValue(deal, 'number_of_ndas', 0);
  const negotiationsCount = safeValue(deal, 'number_of_negotiations', 0);
  const rolodexCount = safeValue(deal, 'rolodex.length', 0);
  const savedCount = safeValue(deal, 'saved.length', 0);
  const ndaOutForReviewCount = safeValue(deal, 'nda_out_for_review.length', 0);
  
  return (
    <div className="mb-6">
      {/* Header */}
      <div className="bg-succession-bg-primary border border-succession-border rounded-t-lg shadow-succession border-b-0">
        <div className="px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-succession-text-primary">
            {dealRoomBrandedName}
            {isClosed && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-error text-white">
                CLOSED
              </span>
            )}
          </h1>
          
          {!isClosed && companySettings.enableCloseDeal && (
            <div className="relative">
              <button
                className="text-succession-text-muted hover:text-succession-text-primary hover:bg-succession-bg-tertiary p-2 rounded-full transition-colors duration-150"
                onClick={() => {
                  if (window.confirm("Are you sure you want to close this deal?")) {
                    closeDeal({
                      variables: {
                        dealId: deal.id
                      }
                    });
                  }
                }}
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main navigation menu */}
      <div className="bg-succession-bg-primary border-l border-r border-succession-border shadow-succession">
        <div className="overflow-x-auto">
          <div className="flex min-w-max">
            <Link 
              to={`/succession/deals/${dealName}/teaser`}
              className={`relative flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-150 ${
                isActive('teaser') 
                  ? 'text-succession-primary border-succession-primary font-semibold' 
                  : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
              }`}
            >
              <div className="flex flex-col">
                <span className="hidden md:inline"><FormattedMessage id="dealMenu.viewTeaser" /></span>
                <span className="inline md:hidden"><FormattedMessage id="dealMenu.teaser" /></span>
              </div>
            </Link>
            
            <Link 
              to={`/succession/deals/${dealName}/evaluate-matches`}
              className={`relative flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-150 ${
                (isActive('evaluate-matches') || isActive('invite') || isActive('following') || isActive('saved'))
                  ? 'text-succession-primary border-succession-primary font-semibold' 
                  : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
              }`}
            >
              <div className="flex flex-col">
                <span className="hidden md:inline"><FormattedMessage id="dealMenu.inviteAdvisors" /></span>
                <span className="inline md:hidden"><FormattedMessage id="dealMenu.invite" /></span>
                {matchesCount > 0 && (
                  <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-succession-primary text-white">
                    {matchesCount}
                  </span>
                )}
                {invitesCount > 0 && (
                  <span className="text-xs mt-1 text-succession-text-muted">
                    <FormattedMessage id="dealMenu.unanswered" values={{ count: invitesCount }} />
                  </span>
                )}
              </div>
            </Link>
            
            <Link 
              to={`/succession/deals/${dealName}/review-proposals`}
              className={`relative flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-150 ${
                (isActive('review-proposals') || isActive('shortlisted') || isActive('archived'))
                  ? 'text-succession-primary border-succession-primary font-semibold' 
                  : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
              }`}
            >
              <div className="flex flex-col">
                <span className="hidden md:inline"><FormattedMessage id="dealMenu.reviewInterests" /></span>
                <span className="inline md:hidden"><FormattedMessage id="dealMenu.review" /></span>
                {proposalsCount > 0 && (
                  <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-succession-primary text-white">
                    {proposalsCount}
                  </span>
                )}
                {shortlistedCount > 0 && (
                  <span className="text-xs mt-1 text-succession-text-muted">
                    <FormattedMessage id="dealMenu.shortlisted" values={{ count: shortlistedCount }} />
                  </span>
                )}
              </div>
            </Link>
            
            <Link 
              to={`/succession/deals/${dealName}/nda-out-for-review`}
              className={`relative flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-150 ${
                (isActive('nda-out-for-review') || isActive('nda-awaiting-signature') || isActive('nda-completed'))
                  ? 'text-succession-primary border-succession-primary font-semibold' 
                  : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
              }`}
            >
              <div className="flex flex-col">
                <span className="hidden md:inline"><FormattedMessage id="dealMenu.executeNdas" /></span>
                <span className="inline md:hidden"><FormattedMessage id="dealMenu.ndas" /></span>
                {ndasCount > 0 && (
                  <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-succession-primary text-white">
                    {ndasCount}
                  </span>
                )}
                {ndaOutForReviewCount > 0 && (
                  <span className="text-xs mt-1 text-succession-text-muted">
                    <FormattedMessage id="dealMenu.outForReview" values={{ count: ndaOutForReviewCount }} />
                  </span>
                )}
              </div>
            </Link>
            
            <Link 
              to={`/succession/deals/${dealName}/negotiate`}
              className={`relative flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors duration-150 ${
                isActive('negotiate') 
                  ? 'text-succession-primary border-succession-primary font-semibold' 
                  : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
              }`}
            >
              <div className="flex flex-col">
                <span className="font-medium"><FormattedMessage id="dealMenu.negotiate" /></span>
                {negotiationsCount > 0 && (
                  <span className="ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-succession-primary text-white">
                    {negotiationsCount}
                  </span>
                )}
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom tabs navigation for the current section */}
      {(isActive('evaluate-matches') || isActive('invite') || isActive('following') || isActive('saved')) && (
        <div className="bg-succession-bg-primary border border-succession-border rounded-b-lg shadow-succession border-t-0 mb-6">
          <div className="border-t border-succession-border">
            <nav className="flex">
              <Link
                to={`/succession/deals/${dealName}/evaluate-matches`}
                className={`px-5 py-3 text-sm font-medium flex-1 text-center border-b-2 transition-colors duration-150 ${
                  isActive('evaluate-matches') 
                    ? 'text-succession-primary border-succession-primary font-semibold'
                    : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
                }`}
              >
                <FormattedMessage id="dealMenu.evaluateMatches" />
                {matchesCount > 0 && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary text-white">
                    {matchesCount}
                  </span>
                )}
              </Link>
              
              <Link
                to={`/succession/deals/${dealName}/following`}
                className={`px-5 py-3 text-sm font-medium flex-1 text-center border-b-2 transition-colors duration-150 ${
                  isActive('following') 
                    ? 'text-succession-primary border-succession-primary font-semibold'
                    : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
                }`}
              >
                <FormattedMessage id="dealMenu.rolodex" />
                {rolodexCount > 0 && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary text-white">
                    {rolodexCount}
                  </span>
                )}
              </Link>
              
              <Link
                to={`/succession/deals/${dealName}/invite`}
                className={`px-5 py-3 text-sm font-medium flex-1 text-center border-b-2 transition-colors duration-150 ${
                  isActive('invite') 
                    ? 'text-succession-primary border-succession-primary font-semibold'
                    : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
                }`}
              >
                <FormattedMessage id="dealMenu.invitedAdvisors" />
                {invitesCount > 0 && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-succession-secondary text-white">
                    {invitesCount}
                  </span>
                )}
              </Link>
              
              <Link
                to={`/succession/deals/${dealName}/saved`}
                className={`px-5 py-3 text-sm font-medium flex-1 text-center border-b-2 transition-colors duration-150 ${
                  isActive('saved') 
                    ? 'text-succession-primary border-succession-primary font-semibold'
                    : 'text-succession-text-secondary border-transparent hover:text-succession-primary'
                }`}
              >
                <FormattedMessage id="dealMenu.saved" />
                {savedCount > 0 && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-succession-secondary text-white">
                    {savedCount}
                  </span>
                )}
              </Link>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

DealMenu.propTypes = {
  deal: PropTypes.object,
  companySettings: PropTypes.object,
  activeTab: PropTypes.string
};

export default DealMenu; 